import { Phone, MapPin, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import LeadForm from "@/components/LeadForm";
import { Reveal, Chapter } from "@/components/Reveal";
import { SITE } from "@/data/site";

export default function ContactPage() {
  return (
    <>
      <Seo
        title="Contact Us | Travel Passport and Visa LLC — 866-334-8472"
        siteName={SITE.siteName}
        description="Contact Travel Passport and Visa LLC for passport, visa, and travel document assistance. Call 866-334-8472 or visit our Washington, DC office at 1629 K St NW, Suite 305."
      />
      <section className="grain bg-navy py-16 lg:py-24" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <span className="inline-block rounded-full border border-gold/40 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-gold">
            Private Service Provider
          </span>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-bold tracking-tight text-white sm:text-5xl" data-testid="contact-h1">
            Contact Us
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Tell us about your travel plans and the documentation assistance you need. Our team can help you understand the next steps.
          </p>
        </div>
      </section>

      <section className="bg-slate-50 py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr]">
          <Reveal>
            <Chapter num="01" label="Reach Us" />
            <div className="space-y-5">
              <a
                href={SITE.phoneTel}
                data-testid="contact-phone-card"
                className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-gold/60 hover:shadow-lg"
              >
                <Phone className="mt-1 h-6 w-6 shrink-0 text-gold-dark" />
                <div>
                  <p className="font-display text-lg font-bold text-navy group-hover:text-gold-dark">{SITE.phoneDisplay}</p>
                  <p className="mt-1 text-sm text-slate-500">Call now to speak with our team</p>
                </div>
              </a>
              <a
                href={SITE.phone2Tel}
                data-testid="contact-phone-card-2"
                className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-gold/60 hover:shadow-lg"
              >
                <Phone className="mt-1 h-6 w-6 shrink-0 text-gold-dark" />
                <div>
                  <p className="font-display text-lg font-bold text-navy group-hover:text-gold-dark">{SITE.phone2Display}</p>
                  <p className="mt-1 text-sm text-slate-500">You can also reach us at our Washington, DC line</p>
                </div>
              </a>
              <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="contact-address-card">
                <MapPin className="mt-1 h-6 w-6 shrink-0 text-gold-dark" />
                <div>
                  <p className="font-display text-lg font-bold text-navy">{SITE.addressLine1}</p>
                  <p className="text-sm text-slate-500">{SITE.addressLine2}</p>
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="contact-private-card">
                <ShieldCheck className="mt-1 h-6 w-6 shrink-0 text-gold-dark" />
                <div>
                  <p className="font-display text-lg font-bold text-navy">Private Service Provider</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-500">{SITE.shortDisclosure}</p>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div id="get-started" className="scroll-mt-28 rounded-2xl bg-white p-7 shadow-xl ring-1 ring-slate-100 sm:p-8" data-testid="contact-form-card">
              <div className="gold-rule mb-5 w-14" />
              <h2 className="font-display text-xl font-bold text-navy sm:text-2xl" data-testid="contact-form-heading">
                Tell Us What You Need Help With
              </h2>
              <p className="mb-6 mt-2 text-sm text-slate-500">
                Complete the form and our team will follow up with next steps.
              </p>
              <LeadForm source="contact-page" testId="contact-lead-form" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
